import React, {
    createContext,
    useEffect,
    useState
} from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router";
import {
    QueryClient,
    QueryClientProvider,
    useQuery
  } from '@tanstack/react-query'
import Cookies from "js-cookie";

import { ProtectedRoute } from "./protected-route";
import { PushNotification } from './push-notification';

// Used for push notifications
export const NotificationContext = createContext(null);
export const handleError = (err, setNotification) => {
  setNotification({
    message : 'An error has occured.',
    error_message : err
  })
  console.log('Error caught in handleError: ', err);
}

import * as CONSTANTS from "./constants";
import * as QUERIES from "./query-functions";
import * as UTILS from "./utils";

import BookOnline from "./book-online/book-online";
import SelectSeats from "./book-online/select-seats";
import EditTickets from './edit-tickets/edit-tickets';
import Extras from "./extras/extras";
import SwitchToIframes from "./components/switch-to-iframes";
import TicketDonations from "./ticket-donations/ticket-donations";
import IframeBasket from "./basket/iframe-basket";
import Basket from "./basket/basket";
import Login from "./login/login";
import Checkout from "./checkout/checkout";
import CreateAccount from "./account/create-account";
import OrderConfirmation from "./order-confirmation/order-confirmation";
import Account from './account/account';
import AccountOrders from './account/account-orders';
import AccountTickets from "./account/account-tickets";
import AccountMemberships from './account/account-memberships';
import AccountHelp from './account/account-help';
import AccountPreferences from './account/account-preferences';
import AccountDetails from './account/account-details';
import AccountPayments from './account/account-payments';
import PasswordReset from "./account/reset-password";
import RedeemGift from "./account/redeem-gift";
import IframeAccount from "./account/iframe-account";
import Queue from "./book-online/queue";
import MultiBuy from "./multibuy/multibuy";

export const queryClient = new QueryClient();

// These queries are used in a lot of files so they are called from this root file.
export const getBasketData = () => {
    return useQuery({
        queryKey: ['basket'],
        queryFn: QUERIES.getBasket,
        staleTime: 86400000
    });
}
export const refetchBasketData = () => {
  queryClient.invalidateQueries({ queryKey: ['basket'], exact: true });
};

export const getCustomer = () => {
    return useQuery({
        queryKey: ['customerData'],
        queryFn: QUERIES.getCustomer,
        staleTime: 86400000,
        retry: false,
    });
}
export const refetchCustomer = () => {
  queryClient.invalidateQueries({ queryKey: ['customerData'], exact: true });
};

const App = () => {
    const [notification, setNotification] = useState(null);
    const [checkedForQueue, setCheckedForQueue] = useState(false);

    const { data: basketData } = getBasketData();

    useEffect(() => {
      if (!basketData) return;

      const ticket_count = basketData.tickets ? basketData.tickets.length : 0;
      const membership_count = basketData.membershipSubscriptions ? basketData.membershipSubscriptions.length : 0;

      let eligible_donations = null;
      if (membership_count && basketData.donations) {
        // If there are memberships in the basket filter out any associated donations so they show as 1 item
        const excluded_donations = UTILS.getExcludedDonations(basketData);
        eligible_donations = basketData.donations.filter((donation) => !excluded_donations.includes(donation.id));
      } else {
        eligible_donations = basketData.donations;
      }
      const donation_count = eligible_donations ? eligible_donations.length : 0;

      const basket_count = ticket_count + membership_count + donation_count;

      Cookies.set("basket_count", basket_count);
      document.dispatchEvent(
          new CustomEvent("basketCount", {
              detail: { count: basket_count },
              bubbles: true,
              composed: true,
          }),
      );

    }, [basketData]);

    return (
      <div className="booking-pathway">
        <BrowserRouter>
            <NotificationContext.Provider value={{ notification, setNotification }}>
              <Routes>
                {/* Event routes */}
                <Route path="book-online/:instanceId?" element={<BookOnline checkedForQueue={checkedForQueue} setCheckedForQueue={setCheckedForQueue} />} />
                <Route path="book-online/:instanceId?/select-seats" element={<SelectSeats checkedForQueue={checkedForQueue} setCheckedForQueue={setCheckedForQueue} />} />
                <Route path="book-online/:instanceId?/edit-tickets" element={<EditTickets />} />

                <Route path="book-online/:instanceId?/extras" element={<Extras />} />
                {CONSTANTS.HAS_TICKET_DONATIONS &&
                  <Route path="book-online/:instanceId?/ticket-donations" element={<TicketDonations />} />
                }
                {CONSTANTS.USE_IFRAMES_FROM &&
                  <Route path="book-online/:instanceId?/next" element={<SwitchToIframes />} />
                }

                {CONSTANTS.EVENT?.attributes?.MultiBuy &&
                  <Route path="book-online/:instanceId?/multibuy" element={<MultiBuy />} />
                }

                {/* Payment routes */}
                {CONSTANTS.USE_IFRAME_BASKET ?
                  <Route path="basket" element={<IframeBasket />} />
                :
                  <Route path="basket" element={<Basket />} />
                }
                <Route path="checkout" element={<Checkout />} />
                <Route path="order-confirmation" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />

                {/* Account routes */}
                {CONSTANTS.USE_IFRAME_ACCOUNT ?
                  <>
                    <Route path="login" element={<IframeAccount />} />
                    <Route path="account" element={<IframeAccount />} />
                  </>
                :
                  <>
                    <Route path="login" element={<Login />} />
                    <Route path="create-account" element={<CreateAccount />} />
                    <Route path="account/password-reset" element={<PasswordReset />} />
                    <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                    <Route path="account/orders" element={<ProtectedRoute><AccountOrders /></ProtectedRoute>} />
                    <Route path="account/orders/tickets/:instanceId?" element={<ProtectedRoute><AccountTickets /></ProtectedRoute>} />
                    <Route path="account/memberships" element={<ProtectedRoute><AccountMemberships /></ProtectedRoute>} />
                    <Route path="account/help" element={<ProtectedRoute><AccountHelp /></ProtectedRoute>} />
                    <Route path="account/preferences" element={<ProtectedRoute><AccountPreferences /></ProtectedRoute>} />
                    <Route path="account/details" element={<ProtectedRoute><AccountDetails /></ProtectedRoute>} />
                    <Route path="account/payments" element={<ProtectedRoute><AccountPayments /></ProtectedRoute>} />
                    <Route path="account/redeem-gift" element={<ProtectedRoute><RedeemGift /></ProtectedRoute>} />
                  </>
                }

              </Routes>
              <PushNotification />
            </NotificationContext.Provider>
        </BrowserRouter>
      </div>
    );
};



if (CONSTANTS.rootNode) {
  ReactDOM.createRoot(CONSTANTS.rootNode).render(
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  );
}
